import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { User, MessageSquare, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: 'nuevo', name: 'Nuevo', color: '#3B82F6' },
  { id: 'contactado', name: 'Contactado', color: '#8B5CF6' },
  { id: 'interesado', name: 'Interesado', color: '#F59E0B' },
  { id: 'propuesta', name: 'Propuesta enviada', color: '#10B981' },
  { id: 'cerrado', name: 'Cerrado', color: '#22C55E' },
  { id: 'perdido', name: 'Perdido', color: '#EF4444' }
];

const KanbanCard = ({ conversation, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: conversation.conversation_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`kanban-card-${conversation.conversation_id}`}
      className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 hover:shadow-md cursor-grab active:cursor-grabbing group transition-shadow"
    >
      <div className="flex items-start gap-3 mb-3">
        <Avatar className="h-9 w-9 flex-shrink-0">
          <AvatarFallback className="bg-[#25D366] text-white text-sm">
            {conversation.contact_name?.charAt(0) || 'C'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-slate-900 truncate">{conversation.contact_name}</h4>
          <p className="text-xs text-slate-500">{conversation.contact_phone}</p>
        </div>
      </div>
      
      {conversation.last_message && (
        <p className="text-sm text-slate-600 line-clamp-2 mb-3">
          {conversation.last_message}
        </p>
      )}
      
      <div className="flex items-center justify-between text-xs text-slate-500">
        {conversation.assigned_agent_name && (
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span className="truncate max-w-[100px]">{conversation.assigned_agent_name}</span>
          </div>
        )}
        {conversation.last_message_time && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{format(new Date(conversation.last_message_time), 'dd/MM HH:mm')}</span>
          </div>
        )}
      </div>
      
      {conversation.unread_count > 0 && (
        <div className="absolute top-2 right-2">
          <span className="bg-[#25D366] text-white text-xs px-2 py-0.5 rounded-full">
            {conversation.unread_count}
          </span>
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({ stage, conversations, children }) => {
  const count = conversations.filter(c => c.stage === stage.id).length;
  
  return (
    <div 
      className="min-w-[320px] w-[320px] flex flex-col bg-slate-100/50 rounded-lg border border-slate-200/60"
      data-testid={`kanban-column-${stage.id}`}
    >
      <div className="p-3 border-b border-slate-200/60">
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: stage.color }}
          />
          <h3 className="font-semibold text-slate-900">{stage.name}</h3>
          <Badge variant="secondary" className="ml-auto bg-white">
            {count}
          </Badge>
        </div>
      </div>
      
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-3">
          {children}
        </div>
      </ScrollArea>
    </div>
  );
};

const Pipeline = () => {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [token]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Determine which column was dropped into
    const overColumnId = over.id;
    let newStage = null;

    // Check if dropped over a column
    STAGES.forEach(stage => {
      if (overColumnId === stage.id || overColumnId.toString().includes(stage.id)) {
        newStage = stage.id;
      }
    });

    // Or check if dropped over another card
    if (!newStage) {
      const overConversation = conversations.find(c => c.conversation_id === overColumnId);
      if (overConversation) {
        newStage = overConversation.stage;
      }
    }

    if (!newStage) return;

    const conversation = conversations.find(c => c.conversation_id === active.id);
    if (!conversation || conversation.stage === newStage) return;

    // Optimistic update
    setConversations(prev => 
      prev.map(c => 
        c.conversation_id === active.id ? { ...c, stage: newStage } : c
      )
    );

    try {
      const response = await fetch(
        `${API_URL}/api/conversations/${active.id}/stage`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ stage: newStage })
        }
      );

      if (response.ok) {
        toast.success(`Movido a ${STAGES.find(s => s.id === newStage)?.name}`);
      } else {
        // Revert on error
        setConversations(prev =>
          prev.map(c =>
            c.conversation_id === active.id ? { ...c, stage: conversation.stage } : c
          )
        );
        toast.error('Error al mover la conversación');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === active.id ? { ...c, stage: conversation.stage } : c
        )
      );
      toast.error('Error al mover la conversación');
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    // Allow dropping onto columns
    const overStage = STAGES.find(s => s.id === over.id);
    if (overStage) return;

    // Or over other cards
    const activeConv = conversations.find(c => c.conversation_id === active.id);
    const overConv = conversations.find(c => c.conversation_id === over.id);

    if (!activeConv || !overConv || activeConv.stage === overConv.stage) return;
  };

  const activeConversation = activeId 
    ? conversations.find(c => c.conversation_id === activeId)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="pipeline-container">
      <div className="p-6 border-b border-slate-200 bg-white">
        <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Pipeline de Ventas</h1>
        <p className="text-slate-500 mt-1">Arrastra las tarjetas para cambiar la etapa del lead</p>
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden p-6 bg-slate-50">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-6 h-full items-start">
            {STAGES.map(stage => {
              const stageConversations = conversations.filter(c => c.stage === stage.id);
              
              return (
                <SortableContext
                  key={stage.id}
                  id={stage.id}
                  items={stageConversations.map(c => c.conversation_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <KanbanColumn stage={stage} conversations={conversations}>
                    {stageConversations.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Sin leads</p>
                      </div>
                    ) : (
                      stageConversations.map(conv => (
                        <KanbanCard
                          key={conv.conversation_id}
                          conversation={conv}
                          isDragging={activeId === conv.conversation_id}
                        />
                      ))
                    )}
                  </KanbanColumn>
                </SortableContext>
              );
            })}
          </div>

          <DragOverlay>
            {activeConversation ? (
              <div className="bg-white p-4 rounded-lg shadow-lg border border-slate-200 w-[288px] rotate-3">
                <div className="flex items-start gap-3 mb-3">
                  <Avatar className="h-9 w-9 flex-shrink-0">
                    <AvatarFallback className="bg-[#25D366] text-white text-sm">
                      {activeConversation.contact_name?.charAt(0) || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 truncate">{activeConversation.contact_name}</h4>
                    <p className="text-xs text-slate-500">{activeConversation.contact_phone}</p>
                  </div>
                </div>
                {activeConversation.last_message && (
                  <p className="text-sm text-slate-600 line-clamp-2">
                    {activeConversation.last_message}
                  </p>
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default Pipeline;
